<template>
  <div class="pt-2">
    <FloatingActionBar
      :selected="selected"
      :totalItems="totalItems"
      :showDeleteSelected="true"
      :show-assign="false"
      :showDeleteAll="false"
      :showUpdateStatus="false"
      :showSelectAll="false"
      @delete-selected="handleShowDialogDelete"
    />

    <!-- Products Table -->
    <div class="overflow-x-auto overflow-y-auto">
      <v-data-table
        :headers="headers"
        :items="products"
        item-key="_id"
        :footer-props="{ itemsPerPageOptions: [5, 10, 15, 30] }"
        :options.sync="options"
        :server-items-length="totalItems"
        :loading="loadTable"
        class="table-radius border border-[#e0e0e0]"
        fixed-header
        show-select
        v-model="selected"
      >
        <template v-for="(header, index) in headers" v-slot:[`header.${header.value}`]>
          <p
            :key="index"
            class="font-title"
            v-if="header.text?.length > 15"
            v-tooltip="{ text: header.text }"
          >
            {{ header.text }}
          </p>
          <p :key="index" v-else class="font-title">{{ header.text }}</p>
        </template>
        <template v-slot:item.productName="{ item }">
          <td class="px-4 py-3 table-font">{{ item.productName }}</td>
        </template>
        <template v-slot:item.productMaker="{ item }">
          <td class="px-4 table-font">
            {{ getPartnerNames(item.productMaker) }}
          </td>
        </template>
        <template v-slot:item.actions="{ item }">
          <td class="pl-4">
            <ActionButton
              :item="item"
              :canEdit="canEdit"
              :canDelete="canDelete"
              :handleEdit="() => editProduct(item)"
              :handleDelete="() => deleteProduct(item)"
              :showEditButton="canEdit"
              :showDeleteButton="canDelete"
            />
          </td>
        </template>
      </v-data-table>
    </div>

    <!-- Bulk Upload Dialog -->
    <v-dialog v-model="openBulkCreate" max-width="600" persistent>
      <UploadProducts @close="closeBulkUpload" />
    </v-dialog>

    <!-- Bulk Delete Confirmation Dialog -->
    <AlertPopUp
      :showConfirmationDialog="showDeleteDialog"
      :confirmationTitle="'Confirm Delete'"
      :confirmationMessage="`Do you want to delete selected products (${selected.length})?`"
      :confirmationActionText="'DELETE'"
      :confirmationActionColor="'red'"
      :performAction="deleteSelectedProducts"
      :cancel="cancelDeleteDialog"
      :loading="loading"
    />
  </div>
</template>

<script>
import ActionButton from '../../ActionButton.vue'
import UploadProducts from './uploadProducts.vue'
import { debounce, hasPermission } from '@/utils/common'
import FloatingActionBar from '../CollectionManagement/CaseModules/FloatingActionBar.vue'
import AlertPopUp from '@/components/AlertPopUp.vue'
import permissionData from '@/utils/permissions'
import { axiosUrl } from '@/utils/workforceApi'

export default {
  name: 'ProductsComponent',
  components: {
    ActionButton,
    FloatingActionBar,
    UploadProducts,
    AlertPopUp
  },
  props: {
    filterProduct: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      userData: this.$store.state.wfmPermissions,
      products: [],
      options: { itemsPerPage: 5, page: 1 },
      totalItems: 0,
      loadTable: true,
      openBulkCreate: false,
      // For FloatingActionBar
      selected: [],
      showDeleteDialog: false,
      loading: false
    }
  },
  watch: {
    options: {
      handler() {
        this.fetchProducts()
      },
      deep: true
    },
    filterProduct: {
      handler() {
        this.options.page = 1
        this.fetchProducts()
      }
    }
  },
  computed: {
    headers() {
      return [
        { text: 'Product Name', value: 'productName', sortable: false, width: '35%' },
        { text: 'Brand', value: 'productMaker', sortable: false, width: '35%' },
        { text: 'Actions', value: 'actions', sortable: false, width: '30%' }
      ]
    },
    canEdit() {
      return hasPermission(this.userData, permissionData.inventoryWrite)
    },
    canDelete() {
      return hasPermission(this.userData, permissionData.inventoryWrite)
    }
  },
  methods: {
    async fetchProducts() {
      try {
        const { page, itemsPerPage } = this.options
        const params = {
          page,
          limit: itemsPerPage > -1 ? itemsPerPage : this.totalItems
        }
        if (this.filterProduct) {
          params.search = this.filterProduct
        }
        this.loadTable = true
        const response = await this.$axios.get(axiosUrl.products.getProducts, {
          params
        })
        this.loadTable = false
        this.products = response.data.products
        this.totalItems = response.data.pagination?.totalCount
      } catch (error) {
        console.error('An error occurred:', error)
      }
    },
    getPartnerNames(productMaker) {
      return productMaker.map((partner) => partner?.maker?.partnerName).join(', ')
    },
    handleInput: debounce(function () {
      this.options.page = 1
      this.fetchProducts()
    }, 1000),
    toggleshowAddProduct() {
      this.$emit('show-add-product')
    },
    uploadBrands() {
      this.openBulkCreate = true
    },
    closeBulkUpload() {
      this.openBulkCreate = false
      this.fetchProducts()
    },
    editProduct(item) {
      this.$emit('edit-product', item)
    },
    deleteProduct(item) {
      console.log('Delete product:', item)
    },
    cancelDeleteDialog() {
      this.showDeleteDialog = false
    },
    handleShowDialogDelete() {
      // Only show the dialog if there are selected items
      if (this.selected.length > 0) {
        this.showDeleteDialog = true
      }
    },
    async deleteSelectedProducts() {
      this.loading = true
      try {
        const deletePromises = this.selected.map((product) =>
          this.$axios.delete(`${axiosUrl.products.deleteProducts}/${product._id}`)
        )

        await Promise.all(deletePromises)
        this.$toast.success('Selected products deleted successfully')
        this.selected = []
        this.showDeleteDialog = false
        this.fetchProducts()
      } catch (error) {
        console.error('Error deleting products:', error)
        this.$toast.error(
          'Error deleting products: ' + (error.response?.data?.message || error.message)
        )
        // Ensure dialog is closed even if there's an error
        this.showDeleteDialog = false
      } finally {
        this.loading = false
      }
    }
  },
  mounted() {
    this.fetchProducts()
  }
}
</script>

<style scoped>
.v-data-table >>> .v-data-table__wrapper > table > tbody > tr > td > .v-data-table__checkbox {
  text-align: center;
}
.table-radius {
  border-radius: 0px !important;
}
.font-title {
  font-size: 0.875rem;
  color: #000;
  margin-bottom: 0px !important;
  max-width: 120px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
